import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { filesService } from "features/FilesInput";
import { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import {
  Badge,
  Form,
  InputGroup,
  OverlayTrigger,
  Popover,
} from "react-bootstrap";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Icons from "shared/icons";
import { formatBytes } from "shared/utils";
import FormGroup from "../../../../shared/ui/FormGroup/FormGroup";
import FileRemovalModal from "../FileRemovalModal/FileRemovalModal";
import "./FilesInput.styles.css";
import { LinearProgress } from "@mui/material";

// TODO Decomposition

const DynamicPopover = forwardRef(
  // @ts-ignore
  ({ popper, children, show: _, ...props }, ref) => {
    useEffect(() => {
      popper.scheduleUpdate();
    }, [children, popper]);

    return (
      <Popover ref={ref} body {...props}>
        {children}
      </Popover>
    );
  }
);

const FilesInput = ({
  name,
  register = (name) => ({}),
  setValue = (name, [], {}) => {},
  uploadedFiles = [],
  control = undefined,
  error = null,
  label = null,
  addon = null,
  displayAs = Form.Group,
  acceptedExtensions = ["jpg"],
  maxSize = 2097152, // 2MB
  max = 3,
  md = 6,
  disabled = false,
  text = null,
  withOCR = false,
  setExternalUploadingState = null,
  setFilesWithErrors = null,
  ...props
}) => {
  const { t } = useTranslation("Shared");
  const filesInputRef = useRef(null);
  const dataTransferRef = useRef(new DataTransfer());
  const [filesToSave, setFilesToSave] = useState([]);
  const [storedFiles, setStoredFiles] = useState([]);
  const [filesToDeleteCount, setFilesToDeleteCount] = useState(0);

  useEffect(() => {
    if (uploadedFiles.length > 0) {
      resetFilesInput();
    }
  }, [uploadedFiles]);

  const [isUploading, setIsUploading] = useState(false);

  const [removalConfirmation, setRemovalConfirmation] = useState({
    fileData: null,
    show: false,
  });
  const onRemovalConfirmationModalHide = useCallback(
    () =>
      setRemovalConfirmation({
        fileData: null,
        show: false,
      }),
    []
  );
  const onRemovalConfirmationModalShow = (fileData) =>
    setRemovalConfirmation({
      show: true,
      fileData,
    });

  const [showValidationsPopover, setShowValidationsPopover] = useState(false);

  const [popover, setPopover] = useState({
    show: false,
    content: null,
  });

  const hidePopover = () => setPopover({ show: false, content: null });
  const showPopover = (content) => setPopover({ show: true, content });

  const setFormValues = (values) => {
    setValue(name, values, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const resetFilesInput = () => {
    setFilesToSave([]);
    setFilesToDeleteCount(0);
    setStoredFiles(uploadedFiles);
    dataTransferRef.current.items.clear();
    filesInputRef.current.files = dataTransferRef.current.files;
  };

  const handleFilesChange = (e) => {
    const addedFilesArray = Array.from(e.target.files);

    if (!addedFilesArray.length) {
      filesInputRef.current.files = dataTransferRef.current.files;
      return;
    }

    addedFilesArray.forEach((addedFile) => {
      const fileIsIPhoneCameraImage =
        /iPhone|iPad/.test(navigator.userAgent) &&
        addedFile.name.split(".").shift() === "image";

      if (fileIsIPhoneCameraImage) {
        addedFile = new File([addedFile], `${Date.now()}_${addedFile.name}`, {
          type: addedFile.type,
        });
      }
      const duplicate = storedFiles.find(
        (fileData) => addedFile.name === fileData.original_name
      );
      if (duplicate) {
        return showPopover(
          t("attachments.popovers.validation_duplicate", {
            fileName: duplicate.original_name,
          })
        );
      }
      const filesLimitExceeded =
        dataTransferRef.current.items.length +
          uploadedFiles.length -
          filesToDeleteCount +
          1 >
        max;
      if (filesLimitExceeded) {
        return showPopover(
          t("attachments.popovers.validation_count", {
            count: max,
          })
        );
      }

      const fileSizeExceeded = addedFile.size > maxSize;

      if (fileSizeExceeded) {
        return showPopover(
          t("attachments.popovers.validation_size", {
            fileName: addedFile.name,
            size: formatBytes(maxSize),
          })
        );
      }

      const invalidFormat = !acceptedExtensions.includes(
        addedFile.name.split(".").pop().toLowerCase()
      );
      if (invalidFormat) {
        return showPopover(
          t("attachments.popovers.validation_format", {
            fileName: addedFile.name,
          })
        );
      }
      setFilesToSave((prev) => [
        ...prev,
        {
          uid: `${(Math.random() + 1).toString(36).substring(8)}_${Date.now()}`,
          file: addedFile,
        },
      ]);
      dataTransferRef.current.items.add(addedFile);
    });

    filesInputRef.current.files = dataTransferRef.current.files;
  };

  const handleFilesSave = (filesPayload) => {
    if (setExternalUploadingState) setExternalUploadingState(true);
    setIsUploading(true);
    (withOCR
      ? filesService.saveFilesWithOCR(filesPayload, {}, setFilesWithErrors)
      : filesService.saveFiles(filesPayload)
    )
      .then((files) => {
        // filesInputRef synchronization with state
        for (let i = 0; i < dataTransferRef.current.items.length; i++) {
          const item = dataTransferRef.current.items[i];
          const file = item.getAsFile();
          const isDuplicate = [...storedFiles, ...files].some(
            (storedFile) => storedFile.original_name === file.name
          );
          if (!isDuplicate) dataTransferRef.current.items.remove(i);
        }
        filesInputRef.current.files = dataTransferRef.current.files;
        setStoredFiles((prev) => [...prev, ...files]);
        setFormValues([...storedFiles, ...files].map((file) => file.id));
      })
      .catch(() => {
        resetFilesInput();
      })
      .finally(() => {
        if (setExternalUploadingState) setExternalUploadingState(false);
        setIsUploading(false);
      });
  };

  useEffect(() => {
    if (filesToSave.length > 0) {
      handleFilesSave(filesToSave);
      setFilesToSave([]);
    }
  }, [filesToSave]);

  const handleFileRemoval = useCallback(
    (fileToRemove) => {
      Array.from(dataTransferRef.current.files).forEach((fileData, idx) => {
        if (fileData.name === fileToRemove.original_name) {
          dataTransferRef.current.items.remove(idx);
        }
      });
      if (
        uploadedFiles.some(
          (uploadedFile) => uploadedFile.id === fileToRemove.id
        )
      ) {
        setFilesToDeleteCount((prev) => prev + 1);
      }
      filesInputRef.current.files = dataTransferRef.current.files;
      const updatedFiles = storedFiles.filter(
        (fileData) => fileData.id !== fileToRemove.id
      );
      setStoredFiles(updatedFiles);
      setFormValues(updatedFiles.map((file) => file.id));
    },
    [storedFiles]
  );

  const onRemove = (fileData) => {
    if (fileData?.isUploaded) {
      onRemovalConfirmationModalShow(fileData);
    } else {
      handleFileRemoval(fileData);
    }
  };

  return (
    <>
      <FileRemovalModal
        handleFileRemoval={handleFileRemoval}
        removalConfirmation={removalConfirmation}
        onHide={onRemovalConfirmationModalHide}
      />
      <FormGroup
        controlId={name}
        label={label}
        addon={addon}
        error={error}
        displayAs={displayAs}
        text={text}
        md={md}
      >
        <InputGroup hasValidation>
          <Controller
            name={name}
            control={control}
            shouldUnregister
            render={() => (
              <OverlayTrigger
                show={popover.show}
                onToggle={hidePopover}
                delay={3000}
                overlay={
                  <DynamicPopover id="popover-contained">
                    {popover.content}
                  </DynamicPopover>
                }
              >
                <div
                  className={`files-input ${error ? "is-invalid" : ""}`}
                  aria-disabled={disabled || isUploading}
                >
                  <p>{isUploading ? t("loading") : t("add_attachment")}</p>
                  <Form.Control
                    type="file"
                    multiple
                    ref={filesInputRef}
                    isInvalid={!!error}
                    onChange={handleFilesChange}
                    disabled={disabled || isUploading}
                    accept={
                      /Android\s14/i.test(navigator.userAgent)
                        ? // https://stackoverflow.com/questions/77886145/chrome-on-android-14-does-not-show-the-option-to-open-the-camera-when-uploading
                          "image/*,text/plain"
                        : acceptedExtensions.map((ext) => `.${ext}`).join(", ")
                    }
                    {...props}
                  />
                </div>
              </OverlayTrigger>
            )}
          />

          <OverlayTrigger
            show={showValidationsPopover}
            onToggle={() => setShowValidationsPopover(false)}
            delay={2000}
            overlay={
              <Popover body>
                {t("attachments.popovers.validation", {
                  count: max,
                  size: formatBytes(maxSize),
                  formats: acceptedExtensions
                    .map((ext) => `.${ext}`)
                    .join(", "),
                })}
              </Popover>
            }
          >
            <InputGroup.Text
              as="button"
              type="button"
              onClick={() => !disabled && setShowValidationsPopover(true)}
            >
              <FontAwesomeIcon icon={Icons.circleInfo} className="text-muted" />
            </InputGroup.Text>
          </OverlayTrigger>
          <Form.Control.Feedback type="invalid">
            {error?.message}
          </Form.Control.Feedback>
        </InputGroup>
        <Form.Text className="w-100">
          <div className="my-2 gap-1">
            {storedFiles?.map((fileData) => {
              return (
                <Badge
                  pill
                  className="me-2 files-input__badge"
                  bg={fileData?.isUploaded ? "primary" : "secondary"}
                  key={fileData.id}
                >
                  <a
                    target="_blank"
                    href={fileData.download_link}
                    className="text-white"
                  >
                    {fileData.original_name}
                  </a>
                  {!disabled && (
                    <button
                      type="button"
                      className="files-input__badge-button"
                      onClick={() => onRemove(fileData)}
                    >
                      <FontAwesomeIcon className="ms-3" icon={Icons.xmark} />
                    </button>
                  )}
                </Badge>
              );
            })}
          </div>
          {isUploading && <LinearProgress />}
        </Form.Text>
      </FormGroup>
    </>
  );
};

export default FilesInput;
